import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadClaimEventStatuses, loadClaimEventTypes } from '../../../../../store/directusService';
import {
    createSelfEmploymentClaimQueueItem,
    loadEligibilityGroup,
    searchClaimQueue
} from '../../../../../store/participantService';
import { clearValidationErrors, updatePlacement } from '../../../../../store/recruitmentService';
import { loadUserById } from '../../../../../store/userService';
import { calcEndDate } from '../../../../../utils/dateFunctions';
import { getNameFromId, getObjFromId } from '../../../../../utils/directusFunctions';
import {
    ADDRESS_LINE_1_MAX_LENGTH,
    ADDRESS_LINE_2_MAX_LENGTH,
    ADDRESS_LINE_3_MAX_LENGTH,
    CITY_MAX_LENGTH,
    MAX_BUSINESS_NAME,
    MAX_EMAIL,
    MAX_NAME,
    MAX_PHONE,
    MAX_POSTCODE
} from '../../../../../utils/formValidation/commonConstants';
import { formatFloat } from '../../../../../utils/numberUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../../utils/userRoles';
import Button from '../../../../formElements/Button';
import DateSelect from '../../../../formElements/DateSelect';
import RadioButtons from '../../../../formElements/RadioButtons';
import SingleSelect from '../../../../formElements/SingleSelect';
import StaticField from '../../../../formElements/StaticField';
import Switch from '../../../../formElements/Switch';
import TextInputField from '../../../../formElements/TextInputField';
import { validate } from '../validation/validateOwnPlacement';

import form from '../../../../../commonStyles/formStyles.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const restrictedClaimsRoles = [QUALITY, SUPERUSER];
const adviserRoles = [MANAGER, QUALITY, SUPERUSER];
const restrictedClaimStatuses = ['verified', 'claimed', 'paid', 'unclaimable'];
const restrictedClaimTypes = ['job outcome', 'self employment'];
const _MESSAGE = 'Job placement has been updated';
const _CLAIM_MESSAGE = 'Employment claim queue item created';

const OwnPlacement = ({ row, expanded }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        businessName: '',
        city: '',
        claimableEvent: true,
        hiringManagerEmail: '',
        employmentTypeId: '',
        endDate: '',
        endStatusId: '',
        fixedFlexibleHour: 'fixed',
        fullServiceDeclined: false,
        hiringManagerId: '',
        hiringManagerName: '',
        hiringManagerOfficeNumber: '',
        hiringManagerMobileNumber: '',
        hourlyPay: '',
        hoursPerWeek: '',
        hoursPerWeekId: '',
        jobSectorId: '',
        jobStartEvidence: false,
        leadAdviserId: '',
        leadGeneratedById: '',
        participantId: '',
        postcode: '',
        selfEmploymentClaimCalculationDate: '',
        startDate: '',
        startStatusId: '',
        vacancyId: '',
        vacancyTitle: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [isLeadAdviser, setIsLeadAdviser] = useState(false);
    const [isSelfEmployment, setIsSelfEmployment] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [advisers, setAdvisers] = useState([]);
    const [isExistingLeadAdviserExpired, setIsExistingLeadAdviserExpired] = useState(false);
    const [isEditable, setIsEditable] = useState(true);
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [isClaimButtonDisabled, setIsClaimButtonDisabled] = useState(true);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const currentPlacement = useSelector(
        (state) => state.entities.recruitmentService.currentPlacement
    );
    const claimQueues = useSelector((state) => state.entities.participantService.claimQueues);
    const claimEventTypes = useSelector((state) => state.entities.directusService.claimEventTypes);
    const claimEventStatuses = useSelector(
        (state) => state.entities.directusService.claimEventStatuses
    );

    const ownPlacementDetails = useSelector(
        (state) => state.entities.directusService.ownPlacementDetails
    );
    const placementEndStatusDetails = useSelector(
        (state) => state.entities.directusService.placementEndStatusDetails
    );
    const placementStartStatusDetails = useSelector(
        (state) => state.entities.directusService.placementStartStatusDetails
    );
    const placementEmploymentTypeDetails = useSelector(
        (state) => state.entities.directusService.placementEmploymentTypeDetails
    );
    const jobSectorDetails = useSelector(
        (state) => state.entities.directusService.jobSectorDetails
    );
    const users = useSelector((state) => state.entities.userService.users);
    const usersById = useSelector((state) => state.entities.userService.usersById);
    const hoursPerWeekDetails = useSelector(
        (state) => state.entities.directusService.hoursPerWeekDetails
    );
    const validationErrors = useSelector(
        (state) => state.entities.recruitmentService.validationErrors
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    const eligibilityGroup = useSelector(
        (state) => state?.entities.participantService.eligibilityGroup
    );

    // USE EFFECTS
    useEffect(() => {
        claimEventTypes?.length < 1 && dispatch(loadClaimEventTypes());
        claimEventStatuses?.length < 1 && dispatch(loadClaimEventStatuses());
    }, []);

    useEffect(() => {
        if (!row || expanded === 'placementInfo-close') return;
        setNewEntry(row);
        setInitialStateOnEntry(row);
        const isSelfEmployedType =
            getNameFromId(placementEmploymentTypeDetails, row.employmentTypeId) === 'Self Employed';
        setIsSelfEmployment(isSelfEmployedType);
        const isLeadAnAdviser =
            getNameFromId(ownPlacementDetails, row?.leadGeneratedById) === 'Adviser';
        setIsLeadAdviser(isLeadAnAdviser);
        const names = row.hiringManagerName.split(' ');
        setFirstName(names[0]);
        setLastName(names[1]);
    }, [row, expanded]);

    useEffect(() => {
        if (currentParticipant?.eligibilityGroupId) {
            dispatch(loadEligibilityGroup(currentParticipant.eligibilityGroupId));
        }
    }, [currentParticipant]);

    useEffect(() => {
        if (
            currentParticipant.ptCode &&
            eligibilityGroup?.id &&
            newEntry.selfEmploymentClaimCalculationDate &&
            claimQueues?.length >= 0
        ) {
            const claimExists = claimQueues.length
                ? claimQueues.some(
                      (claim) =>
                          claim.eventType === 'Self Employment' &&
                          claim.ptCode === currentParticipant.ptCode
                  )
                : false;
            const claimableDate = calcEndDate(
                new Date(newEntry.selfEmploymentClaimCalculationDate),
                eligibilityGroup.selfEmployedIncomeRequirementDays
            );
            return setIsClaimButtonDisabled(
                claimExists || !(claimableDate <= new Date().toISOString().split('T')[0])
            );
        } else {
            setIsClaimButtonDisabled(true);
        }
    }, [currentParticipant, eligibilityGroup, claimQueues, currentPlacement]);

    useEffect(() => {
        if (claimEventStatuses?.length < 1) return;
        dispatch(
            searchClaimQueue(
                {
                    ptCodes: [currentParticipant.ptCode]
                },
                0
            )
        );
    }, [claimEventStatuses]);

    useEffect(() => {
        const statusIds = claimEventStatuses
            .filter((el) => restrictedClaimStatuses.includes(el.name.toLowerCase()))
            .map((el) => el.id);
        const filteredClaimQueues = claimQueues.filter(
            (el) =>
                restrictedClaimTypes.includes(el.eventType.toLowerCase()) &&
                statusIds.includes(el.statusId)
        );
        if (
            (filteredClaimQueues.length > 0 && hasRole(restrictedClaimsRoles, roles)) ||
            (filteredClaimQueues.length === 0 && hasRole(acceptedRoles, roles))
        )
            setIsEditable(true);
        else setIsEditable(false);
    }, [claimQueues]);

    useEffect(() => {
        if (users?.length < 1) return;
        setAdvisers(
            users
                .filter((el) => el.userTypes?.find((entry) => adviserRoles.includes(entry.role)))
                .map((el) => ({ id: el.id, name: el.loginEmailAddress }))
                .sort((a, b) => a.name.localeCompare(b.name))
        );
        if (!row?.leadAdviserId) return;
        const adviser = users.find((el) => el.id === row.leadAdviserId);
        if (!adviser && !(row.leadAdviserId in usersById))
            dispatch(loadUserById(row.leadAdviserId));
    }, [users, row.leadAdviserId]);

    useEffect(() => {
        if (
            !isLeadAdviser ||
            isExistingLeadAdviserExpired ||
            !row.leadAdviserId ||
            !(row.leadAdviserId in usersById)
        )
            return;
        const adviserServiceIds = [
            ...usersById[row.leadAdviserId].otherServices,
            usersById[row.leadAdviserId].primaryService
        ].filter((el) => el);
        const isExAdviser = !adviserServiceIds.includes(currentParticipant.serviceId);
        setIsExistingLeadAdviserExpired(isExAdviser);
        if (isExAdviser) {
            const tempAdviser = {
                id: usersById[row.leadAdviserId].id,
                name: usersById[row.leadAdviserId].loginEmailAddress
            };
            setAdvisers((prev) => [...prev, tempAdviser]);
        }
    }, [usersById, isLeadAdviser, row.leadAdviserId, currentParticipant.serviceId]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _MESSAGE) setIsUpdateButtonDisabled(false);
        if (successMessage === _CLAIM_MESSAGE) setIsClaimButtonDisabled(true);
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            dispatch(clearValidationErrors());
        }
    }, [successMessage, validationErrors]);

    useEffect(() => {
        if (expanded === 'placementInfo-close') setErrors({});
    }, [expanded]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onStartDateChange = (date) => {
        clearError('endDate');
        onUpdate('startDate', date);
    };

    const onEndDateChange = (date) => {
        onUpdate('endDate', date);
        if (!date) onUpdate('endStatusId', '');
    };

    const onSwitchFixedOrFlexible = (e) => {
        clearError('hoursPerWeek');
        clearError('hoursPerWeekId');
        const { checked } = e.target;
        checked
            ? setNewEntry((prev) => ({
                  ...prev,
                  hoursPerWeek: null,
                  fixedFlexibleHour: 'flexible'
              }))
            : setNewEntry((prev) => ({ ...prev, hoursPerWeekId: '', fixedFlexibleHour: 'fixed' }));
    };

    const onFirstNameChange = (value) => {
        clearError('firstName');
        setFirstName(value);
    };

    const onLastNameChange = (value) => {
        clearError('lastName');
        setLastName(value);
    };

    const onLeadGeneratedByChange = (chosenId) => {
        onUpdate('leadGeneratedById', chosenId);
        if (getNameFromId(ownPlacementDetails, chosenId) !== 'Adviser') {
            setIsLeadAdviser(false);
            onUpdate('leadAdviserId', '');
        } else setIsLeadAdviser(true);
    };

    const onUpdateLeadAdviser = (chosenId) => {
        if (isExistingLeadAdviserExpired) {
            const existingAdviserId = advisers.find((el) => el.id === row.leadAdviserId)?.id || '';
            if (existingAdviserId) {
                setAdvisers((prev) => prev.filter((el) => el.id !== existingAdviserId));
                setIsExistingLeadAdviserExpired(false);
            }
        }
        onUpdate('leadAdviserId', chosenId);
    };

    const onCreateSelfEmploymentClaim = () => {
        setIsClaimButtonDisabled(true);
        const payload = {
            participantId: currentParticipant.id,
            placementId: newEntry.id
        };
        dispatch(createSelfEmploymentClaimQueueItem(payload));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const payload = {
            ...newEntry,
            participantId: currentParticipant.id,
            hiringManagerName: `${firstName} ${lastName}`
        };
        const newErrors = validate(
            payload,
            isLeadAdviser,
            isSelfEmployment,
            firstName,
            lastName,
            initialStateOnEntry
        );
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        dispatch(updatePlacement(payload, _MESSAGE));
    };

    // RENDER
    return (
        <div>
            <form onSubmit={onSubmit} data-testid="edit-own-placement-form">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <StaticField
                            id="employmentTypeId"
                            label="Employment Type"
                            content={getNameFromId(
                                placementEmploymentTypeDetails,
                                row.employmentTypeId
                            )}
                            customClass="inputStyle"
                        />
                        <div className={form.formColumnSplit}>
                            <DateSelect
                                id="startDate"
                                label="Start Date"
                                disabled={!isEditable}
                                mandatory={true}
                                value={newEntry.startDate || ''}
                                error={errors.startDate}
                                onDateChange={onStartDateChange}
                            />
                            <DateSelect
                                id="endDate"
                                label="End Date"
                                disabled={!isEditable}
                                value={newEntry.endDate || ''}
                                error={errors.endDate}
                                onDateChange={onEndDateChange}
                            />
                        </div>
                        <div
                            className={`${isSelfEmployment ? form.formColumnSplit : ''}`}
                            hidden={!isSelfEmployment}>
                            <DateSelect
                                id="selfEmploymentClaimCalculationDate"
                                label="Self Employed Claim Calculation Date"
                                disabled={!isEditable}
                                mandatory={isSelfEmployment}
                                value={newEntry.selfEmploymentClaimCalculationDate || ''}
                                error={errors.selfEmploymentClaimCalculationDate}
                                onDateChange={(date) =>
                                    onUpdate('selfEmploymentClaimCalculationDate', date)
                                }
                            />
                            <Button
                                id="buttonCreateEmploymentClaim"
                                type="button"
                                customClass="buttonInline"
                                content="Create Self Employment Claim"
                                disabled={!hasRole(adviserRoles, roles) || isClaimButtonDisabled}
                                error={errors.buttonCreateEmploymentClaim}
                                clearError={() => clearError('button')}
                                onClick={onCreateSelfEmploymentClaim}
                            />
                        </div>
                        <SingleSelect
                            id="startStatusId"
                            label="Start Status"
                            placeholder="Select a start status..."
                            disabled={!isEditable}
                            mandatory={true}
                            menuItems={placementStartStatusDetails}
                            selectedId={newEntry.startStatusId}
                            selected={getObjFromId(
                                placementStartStatusDetails,
                                newEntry.startStatusId
                            )}
                            error={errors.startStatusId}
                            onChange={(chosenId) => onUpdate('startStatusId', chosenId)}
                        />
                        <SingleSelect
                            id="endStatusId"
                            label="End Status"
                            placeholder="Select an end status..."
                            disabled={!isEditable || !newEntry.endDate}
                            mandatory={!!newEntry.endDate}
                            menuItems={placementEndStatusDetails}
                            selectedId={newEntry.endStatusId}
                            selected={getObjFromId(placementEndStatusDetails, newEntry.endStatusId)}
                            error={errors.endStatusId}
                            onChange={(chosenId) => onUpdate('endStatusId', chosenId)}
                        />
                        <TextInputField
                            id="vacancyTitle"
                            label="Vacancy Title"
                            placeholder="Enter vacancy title"
                            disabled={!isEditable}
                            mandatory={true}
                            maxLength={MAX_NAME}
                            value={newEntry.vacancyTitle || ''}
                            error={errors.vacancyTitle}
                            onChange={(e) => onUpdate('vacancyTitle', e.target.value)}
                        />
                        <TextInputField
                            id="hourlyPay"
                            label="Hourly Pay"
                            placeholder="Enter hourly pay"
                            disabled={!isEditable}
                            mandatory={true}
                            maxLength={6}
                            value={newEntry.hourlyPay || ''}
                            error={errors.hourlyPay}
                            onChange={(e) => onUpdate('hourlyPay', formatFloat(e.target.value))}
                        />
                        <Switch
                            id="fixedFlexibleHour"
                            label="Fixed or Flexible"
                            leftLabel="Fixed"
                            rightLabel="Flexible"
                            disabled={!isEditable}
                            checked={newEntry.fixedFlexibleHour === 'flexible'}
                            onChange={onSwitchFixedOrFlexible}
                        />
                        {newEntry.fixedFlexibleHour === 'fixed' ? (
                            <TextInputField
                                id="hoursPerWeek"
                                label="Hours per Week"
                                placeholder="Enter hours per week"
                                disabled={!isEditable}
                                mandatory={newEntry.fixedFlexibleHour === 'fixed'}
                                maxLength={5}
                                value={newEntry.hoursPerWeek || ''}
                                error={errors.hoursPerWeek}
                                onChange={(e) =>
                                    onUpdate('hoursPerWeek', formatFloat(e.target.value))
                                }
                            />
                        ) : (
                            <SingleSelect
                                id="hoursPerWeekId"
                                label="Hours per Week"
                                placeholder="Select hours per week..."
                                disabled={!hasRole(acceptedRoles, roles)}
                                mandatory={newEntry.fixedFlexibleHour === 'flexible'}
                                menuItems={hoursPerWeekDetails}
                                selectedId={newEntry.hoursPerWeekId || ''}
                                selected={getObjFromId(
                                    hoursPerWeekDetails,
                                    newEntry.hoursPerWeekId
                                )}
                                error={errors.hoursPerWeekId}
                                onChange={(chosenId) => onUpdate('hoursPerWeekId', chosenId)}
                            />
                        )}
                        <RadioButtons
                            id="claimableEvent"
                            label="Claimable Event?"
                            disabled={!isEditable}
                            value={newEntry.claimableEvent ?? true}
                            onChange={(option) => onUpdate('claimableEvent', option)}
                        />
                        <RadioButtons
                            id="jobStartEvidence"
                            label="Job Start Evidence?"
                            disabled={!isEditable}
                            value={newEntry.jobStartEvidence ?? false}
                            onChange={(option) => onUpdate('jobStartEvidence', option)}
                        />
                        <div hidden={contractConstants?.canIWSBeDeclined === false}>
                            <RadioButtons
                                id="fullServiceDeclined"
                                label="Full Service Declined?"
                                disabled={!isEditable}
                                value={newEntry.fullServiceDeclined ?? false}
                                onChange={(option) => onUpdate('fullServiceDeclined', option)}
                            />
                        </div>
                        <SingleSelect
                            id="jobSectorId"
                            label="Job Sector"
                            placeholder="Select a job sector..."
                            disabled={!isEditable}
                            mandatory={true}
                            menuItems={jobSectorDetails}
                            selectedId={newEntry.jobSectorId || ''}
                            selected={getObjFromId(jobSectorDetails, newEntry.jobSectorId)}
                            error={errors.jobSectorId}
                            onChange={(chosenId) => onUpdate('jobSectorId', chosenId)}
                        />
                        <TextInputField
                            id="businessName"
                            label="Business Name"
                            placeholder="Enter business name"
                            disabled={!isEditable}
                            mandatory={true}
                            maxLength={MAX_BUSINESS_NAME}
                            value={newEntry.businessName || ''}
                            error={errors.businessName}
                            onChange={(e) => onUpdate('businessName', e.target.value)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <TextInputField
                            id="firstName"
                            label="First Name"
                            placeholder="Enter hiring manager first name"
                            disabled={!isEditable}
                            maxLength={MAX_NAME}
                            mandatory={true}
                            value={firstName || ''}
                            error={errors.firstName}
                            onChange={(e) => onFirstNameChange(e.target.value)}
                        />
                        <TextInputField
                            id="lastName"
                            label="Last Name"
                            placeholder="Enter hiring manager last name"
                            disabled={!isEditable}
                            maxLength={MAX_NAME}
                            mandatory={true}
                            value={lastName || ''}
                            error={errors.lastName}
                            onChange={(e) => onLastNameChange(e.target.value)}
                        />
                        <TextInputField
                            id="hiringManagerOfficeNumber"
                            label="Office Number"
                            placeholder="Enter hiring manager office phone number"
                            disabled={!isEditable}
                            maxLength={MAX_PHONE}
                            mandatory={true}
                            value={newEntry.hiringManagerOfficeNumber || ''}
                            error={errors.hiringManagerOfficeNumber}
                            onChange={(e) => onUpdate('hiringManagerOfficeNumber', e.target.value)}
                        />
                        <TextInputField
                            id="hiringManagerMobileNumber"
                            label="Mobile Number"
                            placeholder="Enter hiring manager mobile phone number"
                            disabled={!isEditable}
                            maxLength={MAX_PHONE}
                            value={newEntry.hiringManagerMobileNumber || ''}
                            error={errors.hiringManagerMobileNumber}
                            onChange={(e) => onUpdate('hiringManagerMobileNumber', e.target.value)}
                        />
                        <TextInputField
                            id={'hiringManagerEmail'}
                            label={'Email'}
                            placeholder={'Enter hiring manager email address'}
                            disabled={!isEditable}
                            maxLength={MAX_EMAIL}
                            value={newEntry.hiringManagerEmail || ''}
                            error={errors.hiringManagerEmail}
                            onChange={(e) => onUpdate('hiringManagerEmail', e.target.value)}
                        />
                        <TextInputField
                            id="addressLine1"
                            label="Address Line 1"
                            placeholder="Enter address line 1"
                            disabled={!isEditable}
                            maxLength={ADDRESS_LINE_1_MAX_LENGTH}
                            mandatory={true}
                            value={newEntry.addressLine1 || ''}
                            error={errors.addressLine1}
                            onChange={(e) => onUpdate('addressLine1', e.target.value)}
                        />
                        <TextInputField
                            id="addressLine2"
                            label="Address Line 2"
                            placeholder="Enter address line 2"
                            disabled={!isEditable}
                            maxLength={ADDRESS_LINE_2_MAX_LENGTH}
                            value={newEntry.addressLine2 || ''}
                            error={errors.addressLine2}
                            onChange={(e) => onUpdate('addressLine2', e.target.value)}
                        />
                        <TextInputField
                            id="addressLine3"
                            label="Address Line 3"
                            placeholder="Enter address line 3"
                            disabled={!isEditable}
                            maxLength={ADDRESS_LINE_3_MAX_LENGTH}
                            value={newEntry.addressLine3 || ''}
                            error={errors.addressLine3}
                            onChange={(e) => onUpdate('addressLine3', e.target.value)}
                        />
                        <TextInputField
                            id="city"
                            label="City"
                            placeholder="Enter city"
                            disabled={!isEditable}
                            maxLength={CITY_MAX_LENGTH}
                            mandatory={true}
                            value={newEntry.city || ''}
                            error={errors.city}
                            onChange={(e) => onUpdate('city', e.target.value)}
                        />
                        <TextInputField
                            id="postcode"
                            label="Postcode"
                            placeholder="Enter postcode"
                            disabled={!isEditable}
                            mandatory={true}
                            maxLength={MAX_POSTCODE}
                            value={newEntry.postcode || ''}
                            error={errors.postcode}
                            onChange={(e) => onUpdate('postcode', e.target.value)}
                        />
                        <SingleSelect
                            id="leadGeneratedById"
                            label="Lead Generated By"
                            placeholder="Select a lead..."
                            disabled={!hasRole(adviserRoles, roles) || !isEditable}
                            mandatory={true}
                            menuItems={ownPlacementDetails}
                            selectedId={newEntry.leadGeneratedById}
                            selected={getObjFromId(ownPlacementDetails, newEntry.leadGeneratedById)}
                            error={errors.leadGeneratedById}
                            onChange={onLeadGeneratedByChange}
                        />
                        <div hidden={!isLeadAdviser}>
                            <SingleSelect
                                id="leadAdviserId"
                                label="Adviser"
                                placeholder="Select an adviser..."
                                disabled={!hasRole(adviserRoles, roles) || !isEditable}
                                mandatory={isLeadAdviser}
                                capitalizeList={false}
                                menuItems={advisers}
                                selectedId={newEntry.leadAdviserId}
                                selected={getObjFromId(advisers, newEntry.leadAdviserId)}
                                error={errors.leadAdviserId}
                                onChange={(chosenId) => onUpdateLeadAdviser(chosenId)}
                            />
                        </div>
                    </div>
                </div>
                <Button
                    id="own_placement_update_button"
                    content="Update"
                    disabled={!isEditable || isUpdateButtonDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

OwnPlacement.propTypes = {
    row: PropTypes.object,
    expanded: PropTypes.string
};

export default OwnPlacement;
